import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { ApiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    sponsorId: '',
    phoneNumber: '',
    position: '',
    name: '',
    secondName: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  // 스폰서 ID 자동 설정 로직 (임시 토큰 지원 추가)
  useEffect(() => {
    const setupSponsorId = async () => {
      console.log('🔍 React Mall - 스폰서 ID 감지 시작');
      console.log('- 현재 URL:', location.pathname + location.search);
      console.log('- 인증 상태:', isAuthenticated);
      console.log('- 사용자 정보:', user);
      
      const searchParams = new URLSearchParams(location.search);
      const sponsorFromUrl = searchParams.get('sponsor');
      const originalSponsor = searchParams.get('original_sponsor');
      const authToken = searchParams.get('auth_token');
      const sponsorAuto = searchParams.get('sponsor_auto') === 'true';
      const fromPage = searchParams.get('from');
      const timestamp = searchParams.get('timestamp');
      
      console.log('📊 URL 파라미터 분석:');
      console.log('- sponsor:', sponsorFromUrl);
      console.log('- original_sponsor:', originalSponsor);
      console.log('- auth_token:', authToken ? `${authToken.substring(0, 10)}...` : null);
      console.log('- sponsor_auto:', sponsorAuto);
      console.log('- from:', fromPage);
      console.log('- timestamp:', timestamp);
      
      let selectedSponsor = '';
      let sponsorSource = '';
      
      // 1순위: 임시 토큰이 있고 자동 스폰서 설정이 요청된 경우
      if (authToken && sponsorAuto) {
        try {
          console.log('🔑 임시 토큰 검증 시도...');
          const response = await ApiService.verifyTempToken(authToken);
          
          if (response.success && response.data?.user) {
            const userData = response.data.user;
            selectedSponsor = userData.username;
            sponsorSource = '✅ 임시 토큰에서 로그인한 사용자 정보로 설정';
            
            // 사용자 정보도 폼에 미리 설정
            setFormData(prev => ({
              ...prev,
              sponsorId: selectedSponsor,
              name: userData.name || '',
              email: userData.email || ''
            }));
            
            console.log('🎉 임시 토큰 검증 성공!');
            console.log('- 스폰서 설정:', selectedSponsor);
            console.log('- 사용자 정보 미리 설정 완료');
            return; // 임시 토큰 처리 완료
          }
        } catch (error) {
          console.error('❌ 임시 토큰 검증 실패:', error);
        }
      }
      
      // 2순위: URL 파라미터에 스폰서가 있는 경우
      if (sponsorFromUrl && sponsorFromUrl.trim() !== '') {
        selectedSponsor = sponsorFromUrl.trim();
        sponsorSource = '✅ URL 파라미터에서 설정';
        console.log('🎯 URL 파라미터에서 스폰서 감지:', selectedSponsor);
      } 
      // 3순위: 로그인한 유저가 있는 경우
      else if (isAuthenticated && user && user.username) {
        selectedSponsor = user.username;
        sponsorSource = '✅ 로그인한 사용자에서 설정';
        console.log('🎯 로그인된 사용자를 스폰서로 설정:', selectedSponsor);
      } 
      // 4순위: 원본 스폰서 파라미터가 있는 경우
      else if (originalSponsor && originalSponsor.trim() !== '') {
        selectedSponsor = originalSponsor.trim();
        sponsorSource = '✅ 원본 스폰서 파라미터에서 설정';
        console.log('🎯 원본 스폰서 파라미터 사용:', selectedSponsor);
      }
      
      if (selectedSponsor) {
        setFormData(prev => ({ 
          ...prev, 
          sponsorId: selectedSponsor 
        }));
        console.log('🎉 스폰서 ID 자동 설정 성공:', selectedSponsor);
        console.log('🔖 설정 소스:', sponsorSource);
      } else {
        console.log('⚠️ 스폰서를 찾을 수 없습니다');
        console.log('- URL에 sponsor 파라미터가 없음');
        console.log('- 임시 토큰이 없거나 검증 실패');
        console.log('- 로그인하지 않았거나 사용자 정보가 없음');
      }
    };
    
    setupSponsorId();
  }, [location, isAuthenticated, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    try {
      setIsLoading(true);
      
      // MLM 회원가입 API 호출
      const registrationData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        name: formData.name,
        secondName: formData.secondName,
        phoneNumber: formData.phoneNumber,
        sponsorId: formData.sponsorId,
        position: formData.position
      };

      const response = await ApiService.register(registrationData);
      
      if (response.success) {
        // 회원가입 성공
        alert('MLM 회원가입이 완료되었습니다. 관리자 승인을 기다려주세요.');
        navigate('/login');
      } else {
        setError(response.message || '회원가입 중 오류가 발생했습니다.');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || '회원가입 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Link to="/" className="flex items-center">
            <div className="bg-kahan-primary text-white px-4 py-2 rounded-lg font-bold text-2xl">
              KAHAN
            </div>
            <span className="ml-2 text-gray-600 font-medium text-xl">Mall</span>
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          회원가입
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          이미 계정이 있으신가요?{' '}
          <Link to="/login" className="font-medium text-kahan-primary hover:text-blue-700">
            로그인하기
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-sm text-red-600">{error}</div>
              </div>
            )}

            <div>
              <label htmlFor="sponsorId" className="block text-sm font-medium text-gray-700">
                스폰서 ID *
              </label>
              <input
                id="sponsorId"
                name="sponsorId"
                type="text"
                required
                value={formData.sponsorId}
                onChange={handleChange}
                readOnly={!!formData.sponsorId}
                className={`mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary ${
                  formData.sponsorId ? 'bg-gray-100' : ''
                }`}
                placeholder="스폰서 ID를 입력하세요"
              />
              {formData.sponsorId && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-xs text-green-700 font-medium">
                    ✅ 스폰서가 자동으로 설정되었습니다
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    스폰서 ID: <span className="font-mono font-semibold">{formData.sponsorId}</span>
                  </p>
                  {location.search.includes('auth_token') && (
                    <p className="text-xs text-blue-600 mt-1">
                      🔗 유저 대시보드에서 자동 연결되었습니다
                    </p>
                  )}
                </div>
              )}
              {!formData.sponsorId && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700">
                    ⚠️ 스폰서 ID가 설정되지 않았습니다
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    브라우저 콘솔을 확인하거나 수동으로 입력해주세요
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                사용자명
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
                placeholder="사용자명을 입력하세요"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                이메일
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
                placeholder="이메일을 입력하세요"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                비밀번호
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
                  placeholder="비밀번호를 입력하세요"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                비밀번호 확인
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
                placeholder="비밀번호를 다시 입력하세요"
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                성명 *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
                placeholder="성명을 입력하세요"
              />
            </div>

            <div>
              <label htmlFor="secondName" className="block text-sm font-medium text-gray-700">
                성명 (영문)
              </label>
              <input
                id="secondName"
                name="secondName"
                type="text"
                value={formData.secondName}
                onChange={handleChange}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
                placeholder="영문 성명을 입력하세요 (선택사항)"
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                전화번호 *
              </label>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                required
                value={formData.phoneNumber}
                onChange={handleChange}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
                placeholder="전화번호를 입력하세요 (예: 010-1234-5678)"
              />
            </div>

            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                포지션 선택 *
              </label>
              <select
                id="position"
                name="position"
                required
                value={formData.position}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-kahan-primary focus:border-kahan-primary"
              >
                <option value="">포지션을 선택하세요</option>
                <option value="left">Left (왼쪽)</option>
                <option value="right">Right (오른쪽)</option>
                <option value="auto">Auto (자동 배치)</option>
              </select>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-kahan-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-kahan-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? '가입 중...' : '회원가입'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;